import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cpu, ArrowRight, Wrench, Truck, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { fetchProducts } from '../data/products';
import { trackPageView } from '../lib/analytics';
import { useCMS } from '../hooks/useCMS';
import type { Product } from '../types';

function shuffleProducts<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Home() {
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroError, setHeroError] = useState(false);
  const { getValue, getHtml } = useCMS();
  // 背景圖由後台 CMS 控制（home.hero.image）；無設定＝純科技感設計
  const heroImage = getValue('home.hero.image', '');

  useEffect(() => {
    trackPageView('/');
    fetchProducts().then((data) => {
      const shuffled = shuffleProducts(data);
      setProducts(shuffled.slice(0, 3));
      setAllProducts(shuffled);
      setLoading(false);
    });
  }, []);

  // 市集輪播：每 4 秒隨機換一張卡，展示商品多樣性（分頁隱藏時暫停）
  useEffect(() => {
    if (allProducts.length <= 3) return undefined;
    let slot = 0;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      setProducts((current) => {
        const visibleIds = new Set(current.map((p) => p.id));
        const pool = allProducts.filter((p) => !visibleIds.has(p.id));
        if (pool.length === 0) return current;
        const next = pool[Math.floor(Math.random() * pool.length)];
        const copy = [...current];
        copy[slot % copy.length] = next;
        slot += 1;
        return copy;
      });
    }, 4000);
    return () => window.clearInterval(timer);
  }, [allProducts]);

  // 首頁全部文字由後台 CMS 可編輯（key home.*）；fallback＝現時顯示值
  const trustItems = [
    {
      icon: Cpu,
      title: getValue('home.trust.1.title', '專業 IT 裝備'),
      desc: getValue('home.trust.1.description', '網絡設備、電腦配件、開發板——由入門到工業級，應有盡有。'),
    },
    {
      icon: Wrench,
      title: getValue('home.trust.2.title', '工程器材齊全'),
      desc: getValue('home.trust.2.description', '工具儀器、感應器、自動化模組，工程現場嘅可靠夥伴。'),
    },
    {
      icon: Truck,
      title: getValue('home.trust.3.title', '網購直送全港'),
      desc: getValue('home.trust.3.description', '網上落單，直送到府，快捷穩妥。'),
    },
    {
      icon: ShieldCheck,
      title: getValue('home.trust.4.title', '品質有保證'),
      desc: getValue('home.trust.4.description', '所有產品均經篩選，售後有跟進，買得放心。'),
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-x1493-dark text-white overflow-hidden min-h-[600px] md:min-h-[700px] flex items-center">
        {/* 後台設定嘅背景圖（home.hero.image）——有設定先渲染，載入失敗退回純設計 */}
        {heroImage && !heroError && (
          <img
            src={heroImage}
            alt=""
            aria-hidden="true"
            loading="eager"
            decoding="async"
            onError={() => setHeroError(true)}
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ transform: 'translateZ(0)' }}
          />
        )}
        {/* 圖片模式下疊深色漸變，保持文字清晰 */}
        {heroImage && !heroError && (
          <div className="absolute inset-0 bg-gradient-to-r from-x1493-dark/95 via-x1493-dark/80 to-x1493-dark/55" />
        )}
        {/* 科技感網格背景 */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              'linear-gradient(#2563eb 1px, transparent 1px), linear-gradient(90deg, #2563eb 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-x1493-primary rounded-full blur-[120px] transform translate-x-1/3 -translate-y-1/3" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-x1493-secondary rounded-full blur-[100px] transform -translate-x-1/3 translate-y-1/2" />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-20 md:py-32 relative z-10 w-full h-full">
          <div className="max-w-2xl h-full flex flex-col md:justify-center">
            <div className="flex-1 md:flex-none flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm mb-6 w-fit">
                <Cpu size={16} className="text-x1493-secondary" />
                <span dangerouslySetInnerHTML={getHtml('home.hero.badge', 'Kare Tech｜IT & ENGINEERING')} />
              </div>
              <h1
                className="text-4xl md:text-6xl font-bold leading-tight mb-6"
                dangerouslySetInnerHTML={getHtml('home.hero.title', '專業 IT 裝備<br />工程器材<br />一站式採購')}
              />
              <p
                className="text-xl text-white font-bold mb-8 leading-relaxed bg-black/50 backdrop-blur-sm px-4 py-3 rounded-xl md:bg-transparent md:backdrop-blur-none md:px-0 md:py-0 md:rounded-none"
                dangerouslySetInnerHTML={getHtml(
                  'home.hero.subtitle',
                  '由網絡設備、電腦配件，到工具儀器、開發板同自動化感應器——<br className="hidden md:block" />Kare Tech 匯聚專業 IT 與工程產品，<br className="hidden md:block" />網購直送全港，幫你嘅項目一次搞掂。'
                )}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-auto md:mt-0">
              <Link to="/store" className="btn-primary text-center flex items-center justify-center gap-2">
                {getValue('home.hero.primaryButtonText', '逛商店')}
                <ArrowRight size={20} />
              </Link>
              <Link
                to="/order/query"
                className="btn-outline text-center flex items-center justify-center gap-2"
              >
                {getValue('home.hero.secondaryButtonText', '查詢訂單')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {trustItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-x1493-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-x1493-primary" size={28} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Highlights */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">{getValue('home.products.title', '熱門產品')}</h2>
            <p className="text-stone-600">{getValue('home.products.subtitle', '網絡設備、工具儀器、開發板到自動化感應器，總有一款啱你項目')}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">載入中...</div>
          ) : (
            <>
              <style>{`
                @keyframes home-product-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }
                .home-product-swap { animation: home-product-in 0.55s ease-out both; }
                @media (prefers-reduced-motion: reduce) { .home-product-swap { animation: none; } }
              `}</style>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="home-product-swap">
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="text-center mt-10">
            <Link to="/store" className="btn-outline inline-flex items-center gap-2">
              查看全部產品
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Teaser */}
      <section className="py-16 bg-x1493-dark text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">{getValue('home.about.title', '技術人嘅補給站')}</h2>
              <p
                className="text-white/80 mb-6 leading-relaxed"
                dangerouslySetInnerHTML={getHtml(
                  'home.about.content',
                  'Kare Tech 專注 IT 與工程兩大範疇：無論你係搭建網絡、維修設備、做自動化項目，定係玩開發板嘅創客，呢度都幫你備齊趁手裝備。產品持續上新，有咩想搵，隨時話我哋知。'
                )}
              />
              <Link to="/store" className="btn-secondary inline-flex items-center gap-2">
                {getValue('home.about.buttonText', '去商店逛逛')}
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="bg-white/5 rounded-3xl p-8 border border-white/10 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-x1493-primary flex items-center justify-center shrink-0">
                  <Cpu className="text-white" size={22} />
                </div>
                <div>
                  <p className="font-bold">IT 產品</p>
                  <p className="text-sm text-white/60">網絡・配件・週邊</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-x1493-secondary flex items-center justify-center shrink-0">
                  <Wrench className="text-x1493-dark" size={22} />
                </div>
                <div>
                  <p className="font-bold">工程器材</p>
                  <p className="text-sm text-white/60">工具・儀器・連接器</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-x1493-accent flex items-center justify-center shrink-0">
                  <ShieldCheck className="text-x1493-dark" size={22} />
                </div>
                <div>
                  <p className="font-bold">自動化與開發</p>
                  <p className="text-sm text-white/60">開發板・感應器・模組</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-r from-x1493-primary to-x1493-secondary rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">{getValue('home.cta.title', '項目要開工？裝備一次備齊。')}</h2>
            <p
              className="text-white/90 mb-8 max-w-xl mx-auto"
              dangerouslySetInnerHTML={getHtml(
                'home.cta.description',
                '網上落單，直送全港。產品資料持續上新，想搵嘅嘢未見到？陸續有嚟。'
              )}
            />
            <Link
              to="/store"
              className="inline-flex items-center gap-2 bg-white text-x1493-primary font-bold px-8 py-4 rounded-2xl hover:bg-stone-100 transition"
            >
              <Truck size={22} />
              {getValue('home.cta.buttonText', '即刻逛商店')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
